import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { backendApiGateway } from '~/client/backend';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { Company } from '~/types/company';
import {
  FetchVATReportingFrequencyParams,
  VATReportingFrequency,
  VATReportingFrequencyQueryKeys,
  VATReportingFrequencyType,
} from '~/types/vat-bookkeeping';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder } from '~/utils/jsonapi-query';

const fetchVATReportingFrequency = async (
  params: FetchVATReportingFrequencyParams,
): Promise<VATReportingFrequency[]> => {
  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', params.companyId);
  if (params.year) {
    filtersBuilder.eq('year', params.year);
  }

  const queryParams = buildJsonAPIQueryParams(filtersBuilder);
  const { data: results } = await backendApiGateway.get<DocumentObject>('/api/v1/vat-reporting-frequencies', {
    params: queryParams,
  });

  return deserialize<VATReportingFrequency>(results) as VATReportingFrequency[];
};

export const useVATReportingFrequency = (year: number) => {
  const companyId = useActiveCompanyId();
  return useSuspenseQuery({
    queryKey: VATReportingFrequencyQueryKeys.byYear(companyId, year),
    queryFn: () => fetchVATReportingFrequency({ companyId, year }),
  });
};

export const useVATReportingFrequencies = () => {
  const companyId = useActiveCompanyId();
  return useSuspenseQuery({
    queryKey: VATReportingFrequencyQueryKeys.all(companyId),
    queryFn: () => fetchVATReportingFrequency({ companyId }),
    staleTime: Infinity,
  });
};

interface CreateVATReportingFrequencyParameters {
  companyId: Company['id'];
  year: number;
  frequencyType: VATReportingFrequencyType;
}

const createVATReportingFrequency = async (params: CreateVATReportingFrequencyParameters) => {
  const payload = serialize(
    {
      company: {
        id: params.companyId,
      },
      year: params.year,
      frequencyType: params.frequencyType,
    },
    'vat-reporting-frequency',
    {
      relationships: ['company'],
    },
  );
  const { data: results } = await backendApiGateway.post<DocumentObject>('/api/v1/vat-reporting-frequencies', payload);
  return deserialize<VATReportingFrequency>(results) as VATReportingFrequency;
};

export const useSetVATReportingFrequency = () => {
  const companyId = useActiveCompanyId();
  return useMutation({
    mutationFn: (params: Omit<CreateVATReportingFrequencyParameters, 'companyId'>) =>
      createVATReportingFrequency({ companyId, ...params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VATReportingFrequencyQueryKeys.all(companyId) });
    },
  });
};
